import { ModalDialog } from "@/components/data-display/modal-dialog";
import { Input } from "@/components/inputs/input";
import { Button } from "@/components/ui/button";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChangeEvent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { ProposalPrivateRoomFormData } from "./modal-proposal-private-room.types";
import { ProposalPrivateRoomSchema } from "./modal-proposal-private-room.validation";
import { TrashIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "@/utils/format/currency";
import { getSpaceTakeValue } from "@/utils/get-space-take-value/get-space-take-value";
import {
  calculateCreditPricing,
  DEFAULT_SPACE_TAKE_PCT,
  FIXED_CREDIT_PRICE,
} from "@/features/vision-360/pricing/marginCalculator";
import {
  ActionFunctionArgs,
  Form,
  Link,
  LoaderFunctionArgs,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { getBitlyShortUrl } from "@/utils/get-short-url";
import { useAuth } from "@/features/authentication";
import { apiHubspotIntegrationMs } from "@/services/api-hubspot-integration-ms";
import { createToast } from "vercel-toast";
import { GetPrivateRoomsApi } from "../../../features/private-room/list/adapters/get-privaterooms/get-privaterooms";
import { Info } from "lucide-react";
import { DEFAULT_ROUTES } from "@/constants/routes";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const ids = url.searchParams
    .getAll("spaceIds[]")
    .map((id: string) => Number(id));

  if (ids.length === 0) {
    throw redirect(DEFAULT_ROUTES.PRIVATE_OFFICE_ADMIN);
  }

  const rooms = await GetPrivateRoomsApi({
    others: {
      roomId: ids,
    },
    limit: ids.length,
    offset: 0,
    orderBy: {
      direction: "",
      column: "",
    },
  });

  const roomsWithTake = await Promise.all(
    rooms.data.private_rooms.map(async (item) => {
      const itemOrigin = (item as { origin?: string }).origin;
      const take =
        itemOrigin === "integration"
          ? String(DEFAULT_SPACE_TAKE_PCT)
          : await getSpaceTakeValue(item.space.id);
      return {
        ...item,
        take: Number(take),
      };
    }),
  );

  return {
    initialItems: roomsWithTake,
  };
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();

  const url = new URL(request.url);
  const privateRoomsIds = formData.getAll("privateRoomsIds").join(",");
  const extraCredits = formData.getAll("extraCredits").join(";");
  const userId = formData.get("userId")?.toString() ?? "";
  const dealId = formData.get("dealId")?.toString() ?? "";

  const searchParams = new URLSearchParams();
  const hashedCredits = btoa(extraCredits);

  searchParams.append("user_id", userId);
  searchParams.append("deal_id", dealId);

  const proposalUrl = `${import.meta.env.VITE_PORTAL_URL}/proposal/privaterooms/${privateRoomsIds}?ec=${hashedCredits}&${searchParams}`;

  const resultShortUrl = (
    await getBitlyShortUrl.post("", {
      long_url: proposalUrl,
    })
  ).data.link;

  if (!resultShortUrl) {
    createToast("Error generating URL, please try again", {
      type: "error",
      timeout: 2000,
    });
  }

  return redirect(
    `links?${url.searchParams.toString()}&proposalShortUrl=${encodeURIComponent(resultShortUrl)}&proposalLongUrl=${encodeURIComponent(proposalUrl)}`,
  );
};

type ProposalLoaderData = Awaited<ReturnType<typeof loader>>;

export function ModalProposalPrivateRoom() {
  const { initialItems } = useLoaderData() as ProposalLoaderData;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [selectedItems, setSelectedItems] = useState(initialItems);
  const { userInfo } = useAuth();

  const [dealDataValues, setDealDataValues] = useState<{ id: number }>();

  async function verifyDealUrl(dealUrl: string) {
    const splitDealUrl = dealUrl.split("/").filter((n) => n);
    const deal_id = splitDealUrl[splitDealUrl.length - 1];
    return await apiHubspotIntegrationMs
      .get(`/deals/${deal_id}`)
      .then(function (resp) {
        setDealDataValues(resp.data);
        return {
          status: true,
          message: "Deal verificado",
        };
      })
      .catch(function () {
        return {
          status: false,
          message: "Deal inválido",
        };
      });
  }

  const [dealUrl, setDealUrl] = useState<string>();
  const [dealUrlValidation, setDealUrlValidation] = useState<{
    status: boolean;
    message: string;
  }>();
  const {
    control,
    formState: { isValid },
  } = useForm<ProposalPrivateRoomFormData>({
    resolver: yupResolver(ProposalPrivateRoomSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  function removeItem(item: any) {
    selectedItems.filter((selectedItem: any) => selectedItem !== item);
    setSelectedItems(
      selectedItems.filter((selectedItem: any) => selectedItem !== item),
    );
  }

  const getDealUrlResult = (dealUrl: string) => {
    verifyDealUrl(dealUrl).then((resp) => {
      setDealUrlValidation(resp);
    });
  };

  if (pathname.includes("/links")) {
    return <Outlet />;
  }

  return (
    <ModalDialog
      type="submit"
      title="Proposta de Sala Privativa"
      description={`Revise as opções selecionadas abaixo, preencha os campos necessários e finalize a criação da proposta`}
      open={true}
      handleClose={() => {
        navigate(-1);
      }}
      handleConfirm={() => {}}
      enableButton={isValid}
      maxMidth="max-w-[85rem] p-0"
      showFooter={false}
      titleStyle="text-lg pt-6 px-6 pb-0 zinc-950"
      descriptionStyle="text-sm px-6 text-zinc-500 mt-0"
    >
      <div className="pt-5 pb-6">
        <div className="px-5 mx-1 max-w-[15rem]">
          <label className="text-zinc-600 text-sm">
            Valor do crédito do cliente
          </label>
          <p className="text-sm font-semibold text-zinc-950">
            {formatCurrency(FIXED_CREDIT_PRICE)}
          </p>
          <span className="text-xs text-zinc-500">
            Valor fixo definido pela política comercial
          </span>
        </div>
        <div className="px-5 mx-1 mt-5">
          <label className="text-zinc-600 text-sm">Salas Selecionadas</label>
        </div>
        <Form method="post">
          <input hidden name="userId" value={userInfo?.user?.id} />
          <input hidden name="dealId" value={dealDataValues?.id} />

          <div className="flex flex-col gap-6 table-height-screen">
            <div className="relative w-full overflow-auto">
              <ProposalTable items={selectedItems} onRemoveItem={removeItem} />
            </div>{" "}
            <div className="flex flex-col px-6">
              <div className="flex gap-3 items-end">
                <Link
                  className="flex gap-1 text-blue-800 text-xs items-center underline"
                  to="https://www.notion.so/wobabr/Gera-o-de-propostas-para-contrata-o-de-Salas-Privativas-4a59ce29d5334a87898466b2711de2aa?pvs=4"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Info className="w-4 h-4" />
                  Verificar instruções para geração de propostas
                </Link>
              </div>
            </div>
            <div className="flex flex-col px-6">
              <div className="flex flex-col">
                <div className="flex gap-3 items-end">
                  <div className="flex-initial w-full">
                    <Controller
                      control={control}
                      name="deal_url"
                      render={({ field, fieldState }) => (
                        <Input
                          className="w-full"
                          label="URL da Deal"
                          name="deal_url"
                          type="string"
                          showlabel={true}
                          value={field.value || ""}
                          onChange={(event) => {
                            field.onChange(event);
                            setDealUrl(event.target.value);
                          }}
                          error={
                            fieldState.error ? fieldState.error.message : ""
                          }
                        />
                      )}
                    />
                  </div>
                  <Button
                    className="txt-sm py-2 px-4 flex-initial w-32 min-h-10"
                    onClick={() => {
                      getDealUrlResult(dealUrl!);
                    }}
                    disabled={!isValid}
                    type="button"
                  >
                    Verificar Deal
                  </Button>
                </div>
                {dealUrlValidation?.message && (
                  <div
                    className={`text-sm ${dealUrlValidation?.status ? "text-lime-500" : "text-red-500"} pt-2`}
                  >
                    {dealUrlValidation?.message}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-6 border-t pt-6 flex gap-3 justify-end items-center px-6">
            <Button
              className="txt-sm bg-white border border-zinc-200 box-shadow-none text-black text-sm hover:bg-gray-100 py-2 px-4 min-h-10"
              onClick={() => navigate(-1)}
            >
              Cancelar
            </Button>

            <Button
              className="txt-sm py-2 px-4 min-h-10"
              type={"submit"}
              disabled={!isValid}
            >
              Confirmar
            </Button>
          </div>
        </Form>
      </div>
    </ModalDialog>
  );
}

const ProposalTable = ({
  items,
  onRemoveItem,
}: {
  items: ProposalLoaderData["initialItems"];
  onRemoveItem: (item: any, index: number) => void;
}) => {
  return (
    <Table id="table-proposal" className="w-full caption-bottom text-sm">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[24px]" />
          <TableHead className="text-zinc-600">Sala</TableHead>
          <TableHead className="text-zinc-600">Espaço</TableHead>
          <TableHead className="text-zinc-600">Bairro</TableHead>
          <TableHead className="text-zinc-600">Cidade</TableHead>
          <TableHead className="text-zinc-600">Capacidade</TableHead>
          <TableHead className="text-zinc-600">Tamanho</TableHead>
          <TableHead className="text-zinc-600">Créditos</TableHead>
          <TableHead className="text-zinc-600">Valor do Cliente</TableHead>
          <TableHead className="text-zinc-600">Valor Extra R$</TableHead>
          <TableHead className="text-zinc-600">Créditos Extras</TableHead>
          <TableHead className="text-blue-700 font-medium">Total R$</TableHead>
          <TableHead className="text-blue-700 font-medium">
            Total Créditos
          </TableHead>
          <TableHead className="text-green-700">Margem</TableHead>
          <TableHead className="text-zinc-600">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item, index) => (
          <ProposalTableRow
            key={item.id}
            item={item}
            onRemove={() => {
              onRemoveItem(item, index);
            }}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export type ProposalItem = ProposalLoaderData["initialItems"][number] & {
  client_price?: number;
  extra_credits?: number;
  total_price?: number;
  total_credits?: number;
  margin?: number;
  extra_value?: number;
};

export const calculateValues = (baseItem: ProposalItem, extraValue = 0) => {
  const values = calculateCreditPricing(
    baseItem.monthly_price,
    baseItem.take,
    extraValue,
  );

  return {
    ...baseItem,
    // Display quirk kept from the original implementation: the visible client
    // price uses the item's own credits from the loader, not the derived count.
    client_price: FIXED_CREDIT_PRICE * baseItem.credits,
    extra_credits: values.extraCredits,
    total_price: values.totalPrice,
    total_credits: values.totalCredits,
    margin: values.margin,
    extra_value: values.extraValue,
    credits: values.credits,
  };
};

export const ProposalTableRow = ({
  item,
  onRemove,
}: {
  item: ProposalItem;
  onRemove: () => void;
}) => {
  const [rowData, setRowData] = useState<ProposalItem>(calculateValues(item));

  const handleExtraValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const extraPrice = Number(event.target.value);
    const newData = calculateValues(rowData, extraPrice);
    setRowData(newData);
  };

  return (
    <TableRow>
      <input type="hidden" name="privateRoomsIds" value={item.id} />
      <input
        type="hidden"
        name="extraCredits"
        value={`${item.id},${rowData.total_credits}`}
      />
      <TableCell></TableCell>
      <TableCell>
        <div className="flex flex-col">
          {rowData.label}
          <span className="text-gray-500">{rowData.id}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          {rowData.space.name}
          <span className="text-gray-500">{rowData.space.id}</span>
        </div>
      </TableCell>
      <TableCell>{rowData.space.neighborhood}</TableCell>
      <TableCell>{rowData.space.cityname}</TableCell>
      <TableCell>{rowData.capacity}</TableCell>
      <TableCell>
        {rowData.size && rowData.size !== null ? `${rowData.size} m2` : "-"}
      </TableCell>
      <TableCell>Mensal: {rowData.credits}</TableCell>
      <TableCell>{formatCurrency(rowData.client_price)}</TableCell>
      <TableCell>
        <Input
          id={`extra-value-${item.id}`}
          label=""
          type="number"
          value={rowData.extra_value || ""}
          onChange={handleExtraValueChange}
          className="w-full"
        />
      </TableCell>
      <TableCell>{rowData.extra_credits}</TableCell>
      <TableCell className="font-bold">
        {formatCurrency(rowData.total_price)}
      </TableCell>
      <TableCell className="font-bold">{rowData.total_credits}</TableCell>
      <TableCell className="font-bold">{rowData.margin ?? "-"}%</TableCell>
      <TableCell>
        <button className="border border-zinc-200 p-2" onClick={onRemove}>
          <TrashIcon className="text-zinc-950 hover:text-zinc-950 w-4 h-4" />
        </button>
      </TableCell>
    </TableRow>
  );
};
